import { getEnv } from "../../runtimeEnv";
import { WF_ACTION_TYPES } from '../../settings/shared/constants';
import { transformAttributes, cleanAttributes } from './workflowValidation';
import { isCustomLlmType } from './workflowUtils';

// ---------------------------------------------------------------------------
// Start mode / third party app resolution
//
// The input node stores the configured event as:
//   triggerType / triggerId  — event id (manual-start, message-event, webhook, ...)
//   triggerConfig.provider   — KAFKA / MQTT / RABBITMQ when a message event is configured
//   triggerConfig.connectedApp — third party app id, when one is selected
//
// startMode for a message event is always MESSAGE_EVENT. The provider stays
// on the node; the selected app id goes to thirdPartyAppId.
// ---------------------------------------------------------------------------

const DEFAULT_START_MODE = 'manual';
const MESSAGE_EVENT_START_MODE = 'MESSAGE_EVENT';

const nonEmpty = (value) => {
  if (value == null) return '';
  const text = String(value).trim();
  return text;
};

export const isMessageEvent = (triggerName, providerName) => {
  const trigger = String(triggerName || '').toLowerCase().replace(/[\s_]/g, '-');
  if (trigger === 'message-event') return true;
  return ['kafka', 'mqtt', 'rabbitmq'].includes(String(providerName || '').toLowerCase());
};

export const resolveEventStart = (inputNode) => {
  const d = inputNode?.data || {};
  const config =
      d.triggerConfig && typeof d.triggerConfig === 'object' && !Array.isArray(d.triggerConfig)
          ? d.triggerConfig
          : {};

  const providerName = nonEmpty(config.provider);
  const triggerName = nonEmpty(d.triggerType) || nonEmpty(d.triggerId) || nonEmpty(d.label);
  const startMode = isMessageEvent(triggerName, providerName)
      ? MESSAGE_EVENT_START_MODE
      : (triggerName || DEFAULT_START_MODE);

  const appId = nonEmpty(config.connectedApp);
  return {
    startMode,
    thirdPartyAppId: appId || null,
  };
};

/** True when the workflow input node is configured as MESSAGE_EVENT. */
export const isMessageEventWorkflow = (nodes) => {
  const inputNode = Array.isArray(nodes)
      ? nodes.find((n) => n?.type === 'input')
      : null;
  return resolveEventStart(inputNode).startMode === MESSAGE_EVENT_START_MODE;
};

// Helper function to build workflow payload
export const createBuildWorkflowPayload = (ProjectID, agentUidToNodeIdMap, consoleQueryRef, projectName) => {
  return (currentNodes, currentEdges, query) => {
    // Clear existing uid mapping before building new one
    agentUidToNodeIdMap.current.clear();
    // Clear window mapping as well
    if (window.agentUidToNodeIdMap) {
      window.agentUidToNodeIdMap.clear();
    }

    // Get all config nodes
    const llmNodes = currentNodes?.filter(n => n.type === 'config' && n.data.configType === 'llm');

    // Prepare LLM config
    const llmConfig = llmNodes?.map((llmNode, index) => {
      const isCustom = isCustomLlmType(llmNode?.data?.llmType);
      return {
        title: llmNode?.data?.llmType !== 'litellm' ? llmNode?.data?.displayName : llmNode?.data?.model?.split('/')[0] || null,
        llmConfig: {
          llmType: isCustom ? 'custom' : llmNode?.data?.llmType,
          model: llmNode?.data?.model || null,
          apiKey: llmNode?.data?.apiKey || null ,
          baseUrl: llmNode?.data?.baseUrl || null,
          apiVersion: llmNode?.data?.apiVersion || null,
          asyncMode: llmNode?.data?.asyncMode || null,
          embedModel: llmNode?.data?.embedModel || null
        }
      };
    });

    // Get handler list in correct order following workflow path from input
    // 1. Find input node
    const inputNode = currentNodes?.find(n => n.type === 'input');

    // Message event → startMode MESSAGE_EVENT. The selected app id → thirdPartyAppId.
    const { startMode, thirdPartyAppId } = resolveEventStart(inputNode);

    // 2. Traverse workflow path from input, collecting handlers in order
    const orderedToolConfigNodes = [];
    const orderedAgentNodes = [];
    const visitedNodes = new Set();

    if (inputNode) {
      // Helper function to collect tool handlers for an agent/task node
      const collectToolHandlers = (nodeId) => {
        const toolEdges = currentEdges?.filter(
            e => e.source === nodeId &&
                e.sourceHandle === 'tool' &&
                e.target
        ) || [];

        const tools = [];
        toolEdges.forEach(toolEdge => {
          const toolConfigNode = currentNodes?.find(
              n => n.id === toolEdge.target &&
                  n.type === 'config' &&
                  n.data.configType === 'tool'
          );

          if (toolConfigNode && !visitedNodes.has(toolConfigNode.id)) {
            visitedNodes.add(toolConfigNode.id);
            tools.push(toolConfigNode);
          }
        });
        return tools;
      };

      // BFS traversal from input to output
      const queue = [{ nodeId: inputNode.id, path: [] }];
      const visited = new Set([inputNode.id]);

      while (queue.length > 0) {
        const { nodeId, path } = queue.shift();
        const currentNode = currentNodes?.find(n => n.id === nodeId);

        // If current node is an agent or task, collect its tool handlers
        if (currentNode && (currentNode.type === 'agent' || currentNode.type === 'task')) {
          const tools = collectToolHandlers(nodeId);
          orderedToolConfigNodes.push(...tools);
        }

        if (currentNode && (currentNode.type === 'agent' || currentNode.type === 'task')) {
          orderedAgentNodes.push(currentNode);
        }

        // Get all outgoing edges from current node (excluding tool edges)
        const outgoingEdges = currentEdges?.filter(
            e => e.source === nodeId &&
                e.sourceHandle !== 'tool' &&
                e.sourceHandle !== 'chatModel' // Exclude LLM edges
        ) || [];

        // Add connected nodes to queue for traversal
        outgoingEdges.forEach(edge => {
          if (!visited.has(edge.target)) {
            visited.add(edge.target);
            queue.push({
              nodeId: edge.target,
              path: [...path, nodeId]
            });
          }
        });
      }
    }

    // Fallback: if no input node or no ordered nodes found, use all tool config nodes
    const toolConfigNodes = orderedToolConfigNodes.length > 0
        ? orderedToolConfigNodes
        : currentNodes?.filter(n => n.type === 'config' && n.data.configType === 'tool') || [];

    const output = toolConfigNodes?.map(cfg => ({
      attributes: cfg.data.handlerName === 'MCPHandler' ? transformAttributes(cfg.data.attributes) : {}
    }));

    // Prepare handler list in correct order
    const handlerList = toolConfigNodes?.map((node, index) => ({
      title: node.data?.displayName || 'handler_' + (index + 1),
      handlerName: node.data?.handlerName || null,
      attribute: node?.data?.handlerName === 'MCPHandler'
          ? output[index]?.attributes
          : cleanAttributes(node.data?.attributes),
      srcPath: node.data?.srcPath || null
    }));

    // Prepare engine list (only for tools connected to task agents)
    const engineList = handlerList?.map((handler, index) => {
      // Find the tool config node corresponding to this handler by displayName
      const toolConfigNode = currentNodes?.find(
          n => n.type === 'config' &&
              n.data.configType === 'tool' &&
              n.data?.displayName === handler.title
      );

      // Check if this tool is connected to a task agent
      const taskEdge = currentEdges?.find(
          e => e.target === toolConfigNode?.id &&
              e.sourceHandle === 'tool'
      );

      const connectedTaskNode = taskEdge
          ? currentNodes?.find(n => n.id === taskEdge.source && n.type === 'task')
          : null;

      // Only create engine for task connections
      if (connectedTaskNode) {
        return {
          engineType: 'TASK',
          title: `Engine-${index + 1}`,
          handler: handler?.title,
          instructions: connectedTaskNode.data?.instructions || []
        };
      }
      return null;
    }).filter(Boolean) || [];

    const agentList = orderedAgentNodes?.map((agent, index) => {
      // Collect handler names of tools connected to this agent (edges: source = agent, sourceHandle = 'tool')
      const toolEdgesFromAgent = currentEdges?.filter(
          e => e.source === agent.id && e.sourceHandle === 'tool' && e.target
      ) || [];
      const connectedHandlerNames = toolEdgesFromAgent
          .map(e => {
            const toolNode = currentNodes?.find(
                n => n.id === e.target && n.type === 'config' && n.data?.configType === 'tool'
            );
            return toolNode?.data?.displayName || null;
          })
          .filter(Boolean)
          .join(', ');
      // Engines only for task nodes: find engine titles for tools connected to this task
      const enginesForNode = agent.type === 'task'
          ? toolEdgesFromAgent
              .map(e => {
                const toolNode = currentNodes?.find(
                    n => n.id === e.target && n.type === 'config' && n.data?.configType === 'tool'
                );
                const handlerName = toolNode?.data?.displayName; // here handlerName is displayName
                const engine = handlerName && engineList?.find(eng => eng.handler === handlerName);
                return engine?.title;
              })
              .filter(Boolean)
          : [];
      // engine only for task nodes
      return {
        title: agent?.data?.title || 'Default Agent',
        role: agent?.data?.role || null,
        goal: agent?.data?.goal || null,
        promptTemplate: agent?.data?.systemPrompt?.trim() || null,
        ...(enginesForNode.length === 0 && {
          llm: llmConfig[index]?.title || llmConfig[0]?.title || null
        }),
        ...(enginesForNode.length === 0 && {
          tool: connectedHandlerNames.length > 0 ? connectedHandlerNames : null
        }),
        agentId: agent.id, // Set agentId to nodeId for direct identification in messages
        name: agent?.data?.title || 'Default Agent',
        description: '',
        ...(enginesForNode.length > 0 && { engines: enginesForNode }),
        outputFormat: agent?.data?.outputType || 'json',
        maxRetry: agent?.data?.maxRetry ?? 1,
        policies: (Array.isArray(agent?.data?.policies) ? agent.data.policies : [])
          .filter((item) => item?.policyId || item?.id || item?.name)
          .map((item) => ({
            policyId: item.policyId || item.id || item.name,
            agentId: agent.id,
            enabled: item.enabled !== false,
            policy: item.policy && typeof item.policy === 'object' ? item.policy : {},
          })),
        human_approval: agent?.data?.humanApproval || false,
        // Capabilities are stored per-agent in node.data.capabilities as a JSON string array.
        // For the payload, return only the capabilities for this agentId as a parsed array.
        capabilities: (() => {
          const capsByAgent = agent?.data?.capabilities || {};
          const rawCaps = capsByAgent[agent.id];
          if (!rawCaps || (typeof rawCaps === 'string' && !rawCaps.trim())) {
            return [];
          }
          if (Array.isArray(rawCaps)) {
            return rawCaps;
          }
          if (typeof rawCaps === 'string') {
            try {
              const parsed = JSON.parse(rawCaps);
              return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
              return [];
            }
          }
          return [];
        })(),
      };
    });

    // Map agent node IDs to their corresponding agent config objects
    const agentIdToConfig = new Map();
    orderedAgentNodes?.forEach((agentNode, index) => {
      if (agentList && agentList[index]) {
        agentIdToConfig.set(agentNode.id, agentList[index]);
      }
    });

    // Build routeConfig based on the first Intelligence Decision agent (typically in a parallel group)
    let routeConfig = null;
    if (orderedAgentNodes && orderedAgentNodes.length > 0) {
      const routingAgentNode = orderedAgentNodes.find(a => a?.data?.intelligenceDecisionFlag);
      if (routingAgentNode) {
        const routingMode =
            routingAgentNode.data?.intelligenceDecisionRoutingFlag ||
            (routingAgentNode.data?.capabilitiesRoutingFlag ? 'capabilities' : 'AI');

        if (routingMode === 'capabilities') {
          // Capabilities-based routing
          routeConfig = {
            mode: 'capabilities'
          };
        } else {
          // AI-based routing uses LLM selection
          routeConfig = {
            mode: 'llm',
            llm: llmConfig[0]?.title || null
          };
        }
      }
    }

    // Function to build agents array with parallel grouping
    const buildAgentsArrayWithParallel = () => {
      if (!orderedAgentNodes || orderedAgentNodes.length === 0) {
        return [];
      }

      // Create a map from agent node ID to agent title
      const agentIdToTitle = new Map();
      orderedAgentNodes.forEach(agent => {
        agentIdToTitle.set(agent.id, agent?.data?.title || 'Default Agent');
      });

      // Build a map of which agents are connected from which source nodes with same sourceHandle
      // Key: `${sourceId}_${sourceHandle}`, Value: array of target agent IDs
      const sourceHandleToTargetAgents = new Map();

      orderedAgentNodes.forEach(agent => {
        // Find all incoming edges to this agent (excluding tool and chatModel edges)
        const incomingEdges = currentEdges?.filter(
            e => e.target === agent.id &&
                e.sourceHandle !== 'tool' &&
                e.sourceHandle !== 'chatModel'
        ) || [];

        incomingEdges.forEach(edge => {
          const key = `${edge.source}_${edge.sourceHandle}`;
          if (!sourceHandleToTargetAgents.has(key)) {
            sourceHandleToTargetAgents.set(key, []);
          }
          sourceHandleToTargetAgents.get(key).push(agent.id);
        });
      });

      // Build the agents array by traversing the workflow
      const agentsArray = [];
      const processedAgents = new Set();

      // Helper function to process agents connected from a source with a specific sourceHandle
      const processAgentsFromSource = (sourceId, sourceHandle) => {
        const key = `${sourceId}_${sourceHandle}`;
        const targetAgentIds = sourceHandleToTargetAgents.get(key) || [];

        // Filter to only include agents that haven't been processed yet
        const unprocessedAgents = targetAgentIds.filter(id => !processedAgents.has(id));

        if (unprocessedAgents.length === 0) {
          return;
        }

        // If multiple agents are connected from the same source with same sourceHandle, they run in parallel
        if (unprocessedAgents.length > 1) {
          const parallelGroup = unprocessedAgents.map(id => agentIdToTitle.get(id));
          agentsArray.push(parallelGroup);
          unprocessedAgents.forEach(id => processedAgents.add(id));
        } else {
          // Single agent runs sequentially
          const agentTitle = agentIdToTitle.get(unprocessedAgents[0]);
          agentsArray.push(agentTitle);
          processedAgents.add(unprocessedAgents[0]);
        }
      };

      // Traverse workflow from input to output to maintain order
      if (inputNode) {
        const queue = [{ nodeId: inputNode.id }];
        const visited = new Set([inputNode.id]);

        while (queue.length > 0) {
          const { nodeId } = queue.shift();

          // Get all outgoing edges (excluding tool and LLM edges)
          const outgoingEdges = currentEdges?.filter(
              e => e.source === nodeId &&
                  e.sourceHandle !== 'tool' &&
                  e.sourceHandle !== 'chatModel'
          ) || [];

          // Group edges by sourceHandle to identify parallel connections
          const edgesBySourceHandle = new Map();
          outgoingEdges.forEach(edge => {
            if (!edgesBySourceHandle.has(edge.sourceHandle)) {
              edgesBySourceHandle.set(edge.sourceHandle, []);
            }
            edgesBySourceHandle.get(edge.sourceHandle).push(edge);
          });

          // Process each group of edges with the same sourceHandle
          edgesBySourceHandle.forEach((edges, sourceHandle) => {
            // Check if any of these edges connect to agents/tasks that are in orderedAgentNodes
            const agentEdges = edges.filter(edge => {
              const targetNode = currentNodes?.find(n => n.id === edge.target);
              const isAgentOrTask = targetNode && (targetNode.type === 'agent' || targetNode.type === 'task');
              const isInOrderedList = orderedAgentNodes.some(agent => agent.id === edge.target);
              return isAgentOrTask && isInOrderedList;
            });

            if (agentEdges.length > 0) {
              processAgentsFromSource(nodeId, sourceHandle);
            }
          });

          // Add connected nodes to queue for traversal
          outgoingEdges.forEach(edge => {
            if (!visited.has(edge.target)) {
              visited.add(edge.target);
              queue.push({ nodeId: edge.target });
            }
          });
        }
      }

      // Fallback: if no input node or traversal didn't capture all agents, add remaining sequentially
      orderedAgentNodes.forEach(agent => {
        if (!processedAgents.has(agent.id)) {
          const agentTitle = agent?.data?.title || 'Default Agent';
          agentsArray.push(agentTitle);
          processedAgents.add(agent.id);
        }
      });

      return agentsArray;
    };

    // Prepare pipe list with parallel grouping
    const agentsArray = buildAgentsArrayWithParallel();

    // Determine whether to stop workflow when goal is not satisfied.
    // This is driven by the "Stop Workflow when goal is not satisfied" switch
    // configured on agent/task nodes in `NodeConfigSidebar` (stored as `stopWorkflow`).
    const stopIfGoalIsNotSatisfied =
        orderedAgentNodes?.some(agent => agent?.data?.stopWorkflow) || false;

    // Prepare pipe list
    const pipeList = [{
      title: 'Pipe-1',
      name: 'superagentx pipe',
      description: '',
      agents: agentsArray.length > 0 ? agentsArray : orderedAgentNodes?.map(agent => agent?.data?.title),
      stopIfGoalIsNotSatisfied,
      router: routeConfig,
      authToken: ''
    }];

    const dependencies = (toolConfigNodes || []).flatMap(cfg => {
      const dep = cfg?.data?.dependencies;
      return Array.isArray(dep) ? dep : dep ? [dep] : [];
    });
    const utilsBaseUrl = getEnv("REACT_APP_UTILS_BASE_URL") || '';

    const envVars = [
      {
        name: 'PLATFORM_URL',
        value: `${utilsBaseUrl.replace(/\/$/, '')}/gateway`,
      },
    ];

    const approvalConfig = (orderedAgentNodes || [])
      .filter((agent) => agent?.data?.humanApproval)
      .map((agent) => {
        const channel = agent?.data?.approvalChannelType
          || agent?.data?.approvalChannels?.[0]?.channel
          || '';
        const defaultEmail = String(localStorage.getItem('loginEmail') || '').trim();
        return {
          agentId: agent.id,
          approvalChannel: channel,
          approvalConfig: (Array.isArray(agent?.data?.approvalChannels) ? agent.data.approvalChannels : [])
            .filter((item) => item?.appId || item?.id)
            .map((item) => ({
              id: item.id || item.appId,
              name: item.name || '',
              hasOauth: item.hasOauth !== false,
              provider: String(item.provider || channel || '').toLowerCase(),
            })),
          ...(channel !== '' && { recipient: agent?.data?.approvalRecipient || '' }),
          ...(channel === '' ? { email: defaultEmail } : {}),
        };
      });
    
    const payload = {
      projectId: ProjectID || null,
      edges: currentEdges,
      nodes: currentNodes,
      action: WF_ACTION_TYPES.init,
      query: query || consoleQueryRef.current || '',
      appConfig: {
        appAuthToken: ProjectID || null,
        startMode,
        thirdPartyAppId,
        startOrStop: "START_EVENT",
        appName: projectName || "Untitled Project",
        appType: "all",
        llm: llmConfig,
        memory: [],
        handlerConfig: handlerList,
        engineConfig: engineList,
        agentConfig: agentList,
        approvalConfig,
        pipeConfig: pipeList
      },
      envVars: envVars,
      dependencies: dependencies.length ? dependencies : null
    };
    return payload;
  };
};